import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MATERIAL_IMPORTS } from '../../../material/material-imports';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { CkEditorComponent } from '../../../shared/ck-editor/ck-editor.component';
import { SolutionListComponent } from '../solution/solution-list/solution-list.component';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormGroup, Validators } from '@angular/forms';

import {
  ProblemModel,
  ProblemRequest,
  TopicModel,
  SubjectModelWithTopics,
  PassageModel,
  SolutionModel
} from '@generativ/much-smarter-models';
import { map, startWith, filter } from 'rxjs/operators';

import { FormService } from '../../../shared/form.service';
import { ProblemService } from '../../../shared/problem.service';
import { Observable } from 'rxjs';
import { AlertService } from '../../../core/alert/alert.service';
import { TopicService } from '../../topic/topic.service';
import { PassageService } from '../../passage/passage.service';
import { ParamsService } from '../../../shared/params.service';

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ...MATERIAL_IMPORTS, CapitalizePipe, SpinnerComponent, CkEditorComponent, SolutionListComponent],
  selector: 'app-problem',
  templateUrl: './problem.component.html',
  styleUrls: ['./problem.component.scss']
})
export class ProblemComponent implements OnInit {

  course: ProblemModel;
  problemId: number;
  problemForm: FormGroup;
  problemSolutions: SolutionModel[];
  pageType: string;
  // answerTypes: IAnswerType[]; DEPRECATED

  topics: TopicModel[];
  searchedTopics: Observable<TopicModel[]>;

  passages: PassageModel[];
  searchedPassages: Observable<PassageModel[]>;

  subjects: SubjectModelWithTopics[];
  subjectSelected: SubjectModelWithTopics;

  readonly ready = signal(false);

  titleInUse = false;

  exerciseId: number;
  errorMessage: string;
  isSubmitting = false;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private formService: FormService,
              private problemService: ProblemService,
              private topicService: TopicService,
              private alertService: AlertService,
              private passageService: PassageService,
              private location: Location,
              private paramsService: ParamsService) {
    this.problemForm = this.formService.createForm(this.formOptions);
  }

  ngOnInit() {

    // this.answerTypes = this.problemService.getAnswerTypes();
    this.passageService.getPassages().subscribe({
      next: passages => {
        this.passages = passages;
        this.searchedPassages = this.problemForm.get('passageDisplayName').valueChanges.pipe(
          filter(item => item && item.length > 0),
          map(name => this.filter(name, this.passages, 'passage')));
      },
      error: err => this.onError(err),
    });

    this.searchedTopics = this.problemForm.get('topicDisplayName').valueChanges.pipe(
      filter(item => item && item.length > 0),
      map(name => this.filter(name, this.topics, 'topic')));

    this.route.data.subscribe((data) => {
      this.pageType = data.type;
      if (this.pageType === 'edit') {
        this.route.params.subscribe((params: Params) => {
          this.problemId = +params['problemId'];

          this.problemService.getProblem(this.problemId).subscribe(
            problemModel => {
              this.problemForm.patchValue(problemModel);
              this.problemForm.get('topicDisplayName').setValue(problemModel.topic.displayName);
              this.problemForm.get('topicDisplayName').enable();
              this.problemForm.get('topicId').setValue(problemModel.topic.id);
              if (problemModel.passage) {
                this.problemForm.get('passageDisplayName').setValue(problemModel.passage.title);
                this.problemForm.get('passageId').setValue(problemModel.passage.id);
              }

              this.problemSolutions = problemModel.solutions;
              this.topicService.getTopicsGroupBySubject().subscribe(
                subjects => {
                  this.subjects = subjects;
                  this.subjectSelected = this.subjects.find(item => item.name === problemModel.topic.subject);

                  // In case you want to choose another topic without changing the previously saved subject
                  this.topics = this.subjectSelected.topics;
                  this.ready.set(true);
                },
                err => this.onError(err)
              );
            },
            err => this.onError(err)
          );
        });
      } else {
        this.exerciseId = +this.paramsService.getParamById('exerciseId', this.route.snapshot);
        this.problemForm.get('exerciseId').setValue(this.exerciseId);
        this.topicService.getTopicsGroupBySubject().subscribe(
          subjects => {
            this.subjects = subjects;
            this.ready.set(true);
          },
          err => this.onError(err)
        );
      }
    });
  }

  onError(error) {
    this.alertService.error(error);
    this.ready.set(true);
  }


  submitForm() {
    if (this.formService.validateFormAndUpdateErrors(this.problemForm)) {
      let problemObservable: Observable<ProblemModel>;
      let message: string;
      this.errorMessage = null;

      if (this.isSubmitting) {
        return;
      }

      if (!this.problemForm.valid) {
        this.errorMessage = 'Errors on form. See fields below.';
        return;
      }

      this.isSubmitting = true;

      if (this.pageType === 'create') {
        const problem = new ProblemRequest.Create();
        problem.parse(this.problemForm.value);
        message = 'Problem created';
        problemObservable = this.problemService.createProblem(problem);
      } else {
        const problem = new ProblemRequest.Update();
        problem.parse(this.problemForm.value);
        message = 'Problem edited successfully';
        problemObservable = this.problemService.updateProblem(this.problemId, problem);
      }
      problemObservable.subscribe(
        problemModel => {
          this.alertService.success(message);
          if (this.pageType === 'create') {
            this.router.navigate([`../${problemModel.id}/solutions/create`], {relativeTo: this.route});
          } else {
            this.location.back();
          }
        },
        err => {
          this.isSubmitting = false;
          if(err.errorCode.includes('field_is_duplicated')){
            err = 'This title is already in use.'
          }
          this.alertService.error(err, false, 'snackbar')
        }
      );
    }
  }

  setTopic(topic: TopicModel) {
    this.problemForm.get('topicId').setValue(topic.id);
  }

  setPassage(passage: PassageModel) {
    this.problemForm.get('passageId').setValue(passage.id);
  }

  onSelectSubject() {
    this.topics = this.subjectSelected.topics;
    this.clearTopicValue();
    this.problemForm.get('topicDisplayName').enable();
    this.searchedTopics = this.problemForm.get('topicDisplayName').valueChanges.pipe(
      startWith(this.problemForm.get('topicDisplayName').value),
      map(name => this.filter(name, this.topics, 'topic')));
  }

  clearTopicValue() {
    this.problemForm.get('topicDisplayName').setValue('');
    this.problemForm.get('topicId').setValue(null);
  }

  clearPassageValue() {
    this.problemForm.get('passageDisplayName').setValue('');
    this.problemForm.get('passageId').setValue(null);
  }

  /**
   * Call filter by content if val is not empty
   * @param val
   */
  filter(val: string, objects: any, type: string) {
    const filteredObjects = objects.filter(obj => {
      const value = type === 'passage' ? (<PassageModel>obj).title : (<TopicModel>obj).displayName;
      return value.toLowerCase().includes(val.toLowerCase());
    });

    return val && objects ? filteredObjects : objects;
  }

  get buttonName(): string {
    return this.pageType === 'edit' ? 'SAVE' : this.pageType.toUpperCase();
  }

  private  get formOptions(): object {
    return {
      'title': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      'displayName': [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      'content': [
        'Content. Click here to edit ',
        Validators.compose([
          Validators.required,
          Validators.maxLength(2048)
        ])
      ],
      'topicDisplayName': [{value: '', disabled: true},
        Validators.required],
      'topicId': [
        null,
        Validators.required
      ],
      'passageId': [
        null
      ],
      'difficulty': [
        1,
        Validators.required
      ],
      'passageDisplayName': [
        ''
      ],
      'exerciseId': [
        null
      ],
      'explanation': [
        null,
        Validators.required
      ]
    };
  }

}
